import { useState, type DragEvent, type FormEvent, type ReactNode } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Modale, Scelta } from '../../componenti/base.tsx'
import { IconaCartella } from '../../componenti/Icone.tsx'
import type * as posta from '../../servizi/posta.ts'
import * as elenco from '../../servizi/posta-elenco.ts'
import '../../stile-posta-elenco.css'

/**
 * Cartelle personali (541; come in Roundcube). Sulle caselle ospitate chi ha l'accesso pieno crea, rinomina ed
 * elimina cartelle sue, anche una sotto un'altra (un livello); le cartelle di sistema non si toccano. Le
 * conversazioni si spostano con «Sposta in…» o trascinando le righe dell'elenco sulle cartelle a sinistra.
 * Cartelle e spostamenti restano nel gestionale: i programmi di posta collegati con l'IMAP non li vedono.
 */

/** Il tipo di dati del trascinamento: gli identificativi delle conversazioni, in JSON. */
export const TIPO_TRASCINAMENTO = 'application/x-cidis-conversazioni'

/** Le cartelle in ordine di albero: ogni cartella personale seguita dalle sue sotto-cartelle. */
export function inOrdineDiAlbero(cartelle: posta.Cartella[]): posta.Cartella[] {
  const fuori: posta.Cartella[] = []
  for (const c of cartelle.filter((x) => !x.madre_id)) {
    fuori.push(c, ...cartelle.filter((x) => x.madre_id === c.id))
  }
  // Una figlia la cui madre non e' nell'elenco (supervisione limitata) resta in fondo.
  return [...fuori, ...cartelle.filter((x) => x.madre_id && !fuori.includes(x))]
}

/** Le voci di un menu «Sposta in…»: le cartelle di destinazione, le sotto-cartelle rientrate. */
export function opzioniCartelle(cartelle: posta.Cartella[]): ReactNode[] {
  return inOrdineDiAlbero(cartelle).filter((c) => c.destinazione)
    .map((c) => <option key={c.id} value={c.id}>{c.madre_id ? ` ${c.nome}` : c.nome}</option>)
}

/** «Sposta in…» della conversazione aperta. */
export function SpostaIn({ cartelle, sposta }: { cartelle: posta.Cartella[]; sposta: (cartellaId: string) => void }) {
  if (!cartelle.some((c) => c.destinazione)) return null
  return (
    <select className="posta-sposta-in" value="" aria-label="Sposta la conversazione in una cartella"
            onChange={(e) => { if (e.target.value) sposta(e.target.value) }}>
      <option value="">Sposta in…</option>
      {opzioniCartelle(cartelle)}
    </select>
  )
}

/** Le cartelle nella colonna di sinistra, con le sotto-cartelle rientrate e il rilascio delle righe trascinate. */
export function AlberoCartelle({ cartelle, sceltaId, scegli, nome, titolo, rilascia, conteggio }: {
  cartelle: posta.Cartella[]
  /** La cartella evidenziata; null con il cestino o con i filtri per bandierina ed etichetta. */
  sceltaId: string | null
  scegli: (id: string) => void
  nome: (c: posta.Cartella) => string
  titolo: (c: posta.Cartella) => string | undefined
  /** Trascinando le righe sulle cartelle: null se qui non si sposta (casella IMAP, sola lettura). */
  rilascia: ((cartellaId: string, thread: string[]) => void) | null
  /** Il numero accanto alla cartella: i non letti; per le «Bozze» quante sono (3/11/2026). */
  conteggio?: (c: posta.Cartella) => number
}) {
  const [sopra, setSopra] = useState<string | null>(null)
  const accetta = (c: posta.Cartella, e: DragEvent) =>
    !!rilascia && c.destinazione && c.id !== sceltaId && e.dataTransfer.types.includes(TIPO_TRASCINAMENTO)
  return (
    <>
      {inOrdineDiAlbero(cartelle).map((c) => {
        const scelta = c.id === sceltaId
        return (
          <button key={c.id}
                  className={`posta-scelta${scelta ? ' attiva' : ''}${c.madre_id ? ' posta-sotto-cartella' : ''}`
                    + `${sopra === c.id ? ' posta-rilascio' : ''}`}
                  aria-current={scelta} onClick={() => scegli(c.id)} title={titolo(c)}
                  onDragOver={(e) => { if (accetta(c, e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setSopra(c.id) } }}
                  onDragLeave={() => setSopra((s) => (s === c.id ? null : s))}
                  onDrop={(e) => {
                    setSopra(null)
                    if (!accetta(c, e)) return
                    e.preventDefault()
                    try {
                      const thread = JSON.parse(e.dataTransfer.getData(TIPO_TRASCINAMENTO)) as string[]
                      if (Array.isArray(thread) && thread.length) rilascia!(c.id, thread)
                    } catch { /* trascinamento di altro: niente */ }
                  }}>
            <IconaCartella />
            <span className="posta-cartella-nome">{nome(c)}</span>
            {(conteggio ? conteggio(c) : c.non_letti) > 0 && <span className="conteggio">{conteggio ? conteggio(c) : c.non_letti}</span>}
          </button>
        )
      })}
    </>
  )
}

const testoErrore = (e: unknown) => (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: riprova.')

/**
 * «Cartelle» della casella: le personali, con «Rinomina» ed «Elimina», e la cartella nuova, anche dentro una
 * personale di primo livello. Eliminare chiede conferma con il numero delle conversazioni che tornano nella
 * Posta in arrivo; le regole che vi spostavano si spengono.
 */
export function GestioneCartelle({ casellaId, cartelle, chiudi, cambiate }: {
  casellaId: string
  cartelle: posta.Cartella[]
  chiudi: () => void
  /** Dopo ogni cambio: la pagina rilegge cartelle ed elenco. */
  cambiate: () => void
}) {
  const personali = inOrdineDiAlbero(cartelle).filter((c) => c.personale)
  const madri = personali.filter((c) => !c.madre_id)
  const [nome, setNome] = useState('')
  const [madre, setMadre] = useState('')
  const [inModifica, setInModifica] = useState<{ id: string; nome: string } | null>(null)
  const [daEliminare, setDaEliminare] = useState<{ cartella: posta.Cartella; conversazioni: number; figlie: number } | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [fatto, setFatto] = useState<string | null>(null)

  async function esegui(f: () => Promise<string | null>) {
    setInCorso(true); setErrore(null); setFatto(null)
    try { setFatto(await f()); cambiate() } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  function crea(ev: FormEvent) {
    ev.preventDefault()
    void esegui(async () => {
      const c = await elenco.creaCartella(casellaId, nome, madre || null)
      setNome('')
      return `Cartella «${c.nome}» creata.`
    })
  }

  function rinomina(ev: FormEvent) {
    ev.preventDefault()
    if (!inModifica) return
    void esegui(async () => {
      const r = await elenco.rinominaCartella(inModifica.id, inModifica.nome)
      setInModifica(null)
      return `Ora si chiama «${r.nome}».`
    })
  }

  async function chiediEliminazione(c: posta.Cartella) {
    setErrore(null); setFatto(null); setInModifica(null)
    try {
      const n = await elenco.contenutoCartella(c.id)
      setDaEliminare({ cartella: c, conversazioni: n.conversazioni, figlie: n.figlie })
    } catch (e) { setErrore(testoErrore(e)) }
  }

  return (
    <Modale titolo="Cartelle della casella" chiudi={chiudi} azioni={<Bottone onClick={chiudi}>Chiudi</Bottone>}>
      <p className="posta-nota">
        Le cartelle personali restano nel gestionale: Outlook, Thunderbird o la posta del telefono collegati alla
        casella non le vedono. Le cartelle di sistema (Posta in arrivo, Inviata, Bozze, Archivio, Indesiderata,
        Cestino del server) non si rinominano e non si eliminano.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {fatto && <Avviso tipo="successo">{fatto}</Avviso>}
      {personali.length === 0 && <p className="posta-nota">Nessuna cartella personale.</p>}
      {personali.length > 0 && (
        <ul className="posta-cartelle-gestione">
          {personali.map((c) => (
            <li key={c.id} className={c.madre_id ? 'posta-sotto-cartella' : undefined}>
              {inModifica?.id === c.id
                ? (
                  <form className="posta-cartella-rinomina" onSubmit={rinomina}>
                    <input value={inModifica.nome} maxLength={elenco.NOME_CARTELLA_MASSIMO} autoFocus
                           aria-label={`Nuovo nome di «${c.nome}»`}
                           onChange={(e) => setInModifica({ id: c.id, nome: e.target.value })} />
                    <Bottone tipo="submit" dimensione="piccolo" variante="primario" inCorso={inCorso}>Salva</Bottone>
                    <Bottone dimensione="piccolo" variante="discreto" onClick={() => setInModifica(null)}>Annulla</Bottone>
                  </form>
                )
                : daEliminare?.cartella.id === c.id
                  ? (
                    <div className="posta-cartella-conferma" role="group" aria-label={`Elimina «${c.nome}»`}>
                      <p>
                        Eliminare «{c.nome}»{daEliminare.figlie
                          ? daEliminare.figlie === 1 ? ' e la sua sottocartella' : ` e le sue ${daEliminare.figlie} sottocartelle`
                          : ''}?
                        {daEliminare.conversazioni
                          ? ` ${daEliminare.conversazioni === 1 ? 'La conversazione che contiene torna' : `Le ${daEliminare.conversazioni} conversazioni che contiene tornano`} nella Posta in arrivo: non si cancella niente.`
                          : ' È vuota.'}
                        {' '}Le regole che vi spostavano la posta si spengono.
                      </p>
                      <Bottone dimensione="piccolo" variante="critico" inCorso={inCorso}
                               onClick={() => void esegui(async () => {
                                 const r = await elenco.eliminaCartella(c.id)
                                 setDaEliminare(null)
                                 return r.conversazioni
                                   ? `Cartella eliminata: ${r.conversazioni === 1 ? 'una conversazione è tornata' : `${r.conversazioni} conversazioni sono tornate`} nella Posta in arrivo.`
                                   : 'Cartella eliminata.'
                               })}>Elimina</Bottone>
                      <Bottone dimensione="piccolo" variante="discreto" onClick={() => setDaEliminare(null)}>Annulla</Bottone>
                    </div>
                  )
                  : (
                    <>
                      <span className="posta-cartella-nome"><IconaCartella /> {c.nome}</span>
                      <span className="posta-cartella-azioni">
                        <button type="button" className="segni-azione" disabled={inCorso} aria-label={`Rinomina «${c.nome}»`}
                                onClick={() => { setDaEliminare(null); setInModifica({ id: c.id, nome: c.nome }) }}>Rinomina</button>
                        <button type="button" className="segni-azione" disabled={inCorso} aria-label={`Elimina «${c.nome}»`}
                                onClick={() => void chiediEliminazione(c)}>Elimina</button>
                      </span>
                    </>
                  )}
            </li>
          ))}
        </ul>
      )}
      <form className="posta-cartella-nuova" onSubmit={crea}>
        <Campo etichetta="Nuova cartella" valore={nome} cambia={setNome} maxLength={elenco.NOME_CARTELLA_MASSIMO} />
        {madri.length > 0 && (
          <Scelta etichetta="Dove" valore={madre} cambia={setMadre} vuota="Al primo livello"
                  opzioni={madri.map((m) => ({ valore: m.id, etichetta: `Dentro «${m.nome}»` }))} />
        )}
        <Bottone tipo="submit" variante="primario" inCorso={inCorso} disabled={!nome.trim()}>Crea la cartella</Bottone>
      </form>
    </Modale>
  )
}
